import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";

import { useGlobalContext } from "app/store/GlobalContext";
import { useInboxReplyContext } from "app/store/InboxReplyContext";
import { isInternalUser } from "common/MetaActions";
import { Icon } from "components/icon/Icon";
import { Loader } from "components/loader/Loader";
import FileUploadErrorModal from "components/shared/fileUploadErrorModal/FileUploadErrorModal";
import { filterAndSortMessages } from "helpers/inbox/messageAndThreadHelpers";
import useSetErrorOnUpload from "hooks/use-set-error-on-upload";
import { useStickyActionBar } from "hooks/use-sticky-action-bar";
import { Attachment } from "models/dashboard/dashboard";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import { MessageItem } from "models/inbox/inbox";
import { useRootStore } from "store";
import CreateThreadMobile, {
  CreateThreadMobileHandle
} from "views/inbox/components/Inbox/main/createThread/mobile";
import Message from "views/inbox/components/Inbox/main/message";
import ThreadActionBar from "views/inbox/components/Inbox/main/threadPreview/ThreadActionBar";
import DiscardMessageModal from "views/inbox/DiscardMessageModal";

export default function ThreadPreviewMobile() {
  const { threadId } = useParams();

  const {
    currentThread,
    isLoadingThread,
    loadingThread,
    fetchThread,
    sendMessage,
    selectedMessageId,
    failedToGetThread
  } = useRootStore((state) => state.inbox);

  // const location = useLocation();

  const { handleNavigation } = useGlobalContext();
  const { isReplying, setIsReplying, isDirty, markDirty, clearDirty } = useInboxReplyContext();

  const { workingCustomer: customer } = useRootStore(
    (state) => state.customers
  );
  const { user, archive } = useRootStore((state) => state);
  const { sendFileToArchive } = archive;

  const {
    customerNumber: customerId,
    internalUsers,
    isLoading: isLoadingCustomer
  } = customer;

  const userInfoId = user.userInfo ? user.userInfo.id : "";

  const { failed, setFailed, failMessage, setFailedWrapper } =
    useSetErrorOnUpload();

  const [isSending, setIsSending] = useState(false);
  // const [copied, setCopied] = useState(false);
  const [canSendMessage, setCanSendMessage] = useState(false);
  const [showConfirmDiscardModal, setShowConfirmDiscardModal] = useState(false);
  const createThreadRef = useRef<CreateThreadMobileHandle>(null);

  // Use the custom hook to manage sticky action bar positioning
  const actionBarFixed = useStickyActionBar([currentThread]);

  const { setThreadStatus, archiveThread, unarchiveThread, updateThreadCategory } = useRootStore(
    (state) => state.inbox
  );

  const subscribers = currentThread?.subscribers || [];

  const subscriberIds = subscribers.map((subscriber) => {
    return subscriber.id;
  });
  // Display if there is a message from Absent subscribers
  const absentSubscribers = internalUsers.filter(
    (internalUser) =>
      internalUser.id !== userInfoId &&
      subscriberIds.includes(internalUser.id) &&
      internalUser.outOfOfficeMessage
  );

  const subscriber = currentThread?.subscribers.find(
    (sub) => sub.id === userInfoId
  );

  const { messages: visibleMessages } = useMemo(() => {
    return filterAndSortMessages(currentThread?.messages ?? []);
  }, [currentThread?.messages]);

  const handleThreadListAction = (action: string) => {
    if (!threadId || !action || !currentThread) {
      return;
    }

    if (currentThread.unread) {
      return;
    }
    setThreadStatus(threadId, action);
  };

  // Copy thread link to clipboard - currently commented out, will be added in future releases
  // const handleCopy = async () => {
  //   if (!currentThread) {
  //     return;
  //   }
  //   try {
  //     const baseThreadUrl = `${window.location.origin}${location.pathname}`;

  //     await navigator.clipboard.writeText(baseThreadUrl);
  //     setCopied(true);

  //     setTimeout(() => setCopied(false), 2000);
  //   } catch (err) {
  //     console.error("Failed to copy!", err);
  //   }
  // };

  const handleOnReply = async (
    body: string,
    users: string[],
    replayThreadId: string,
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[]
  ) => {
    try {
      loadingThread();

      const result = await sendFileToArchive(
        filesToUpload,
        customerId,
        "Inbox",
        setFailedWrapper
      );

      if (!Array.isArray(result)) {
        if (threadId && customerId) {
          fetchThread(customerId, threadId);
        }
        return;
      }

      const uploadedAttachments = result.map((file) => ({
        id: file.id,
        name: file.name,
        type: file.type
      }));

      const allAttachments = [...attachments, ...uploadedAttachments];
      sendMessage(
        user.userInfo ? user.userInfo.id : "",
        body,
        "",
        replayThreadId,
        users,
        customerId,
        allAttachments
      );
    } catch (error) {
      console.error("Error while replying:", error);
      failedToGetThread();
    }
  };

  const handleOnSend = async (
    title: string,
    body: string,
    users: string[],
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[]
  ) => {
    if (!currentThread || !threadId) {
      return;
    }
    setIsSending(true);

    await handleOnReply(body, users, threadId, attachments, filesToUpload)
      .then(() => {
        clearDirty();
        setIsReplying(false);
        setIsSending(false);
      })
      .catch(() => {
        setIsSending(false);
      });
  };

  const handleDiscardClicked = () => {
    if (isDirty) {
      setShowConfirmDiscardModal(true);
      return;
    }
    setIsReplying(false);
  };

  const handleConfirmDiscard = () => {
    clearDirty();
    setShowConfirmDiscardModal(false);
    setIsReplying(false);
  };

  const handleNavigateBack = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    handleNavigation({
      url: -1,
      eventTitle: "Back",
      e: e
    });
  };

  const handleCategoryChange = async (category: number) => {
    if (threadId && customerId) {
      await updateThreadCategory(customerId, encodeURIComponent(threadId), category);
    }
  };

  const handleFlagClick = () => {
    // TODO: Implement flag functionality
    console.log("Flag clicked");
  };

  const handleArchiveClick = () => {
    if (threadId && customerId) {
      archiveThread(customerId, encodeURIComponent(threadId));
      handleNavigation({
        url: "/inbox/my",
        eventTitle: "Inbox_my"
      });
    }
  };

  const handleUndoArchiveClick = async () => {
    if (threadId && customerId) {
      unarchiveThread(customerId, encodeURIComponent(threadId));
      handleNavigation({
        url: "/inbox/my",
        eventTitle: "Inbox_my"
      });
    }
  };

  const handleStatsClick = () => {
    // TODO: Implement stats functionality
    console.log("Stats clicked");
  };

  const handleAttachClick = () => {
    createThreadRef.current?.triggerFileSelect();
  };

  const handleAttachFromArchiveClick = () => {
    createThreadRef.current?.openArchiveModal();
  };

  const handleSaveDraft = () => {
    // TODO: Implement save draft functionality
    console.log("Save draft clicked");
  };

  const handleValidationChange = (isValid: boolean) => {
    setCanSendMessage(isValid);
  };

  useEffect(() => {
    if (currentThread?.isArchived && !isInternalUser()) {
      handleNavigation({
        url: "/inbox/my",
        eventTitle: "Inbox_my"
      });
    }
  }, [currentThread?.isArchived, handleNavigation]);

  useEffect(() => {
    if (threadId && customerId) {
      fetchThread(customerId, threadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, customerId]);

  useEffect(() => {
    if (
      !selectedMessageId ||
      isLoadingThread ||
      !currentThread?.messages?.length
    ) {
      return;
    }

    const messageExists = currentThread.messages.some(
      (msg) => msg.messageId === selectedMessageId
    );

    if (!messageExists) {
      return;
    }

    const element = document.getElementById(`${selectedMessageId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedMessageId, isLoadingThread, currentThread?.messages]);

  if (!threadId) {
    return (
      <p className="placeholder">
        Välj en konversation för att visa dess innehåll.
      </p>
    );
  }

  return (
    <div className="w-100 h-100">
      {(isLoadingThread || isLoadingCustomer) && (
        <div className="d-flex align-items-center justify-content-center">
          <Loader className="mr-3" /> Laddar konversationen...
        </div>
      )}

      {/* Unable to load curent thread of customer */}
      {!isLoadingThread && !currentThread && !isLoadingCustomer && (
        <div>
          Vi kunde inte ladda konversationen! Kontakta support för att få hjälp.
        </div>
      )}

      {failed && (
        <FileUploadErrorModal failMessage={failMessage} setFailed={setFailed} />
      )}

      {currentThread && (
        <div className="thread-preview-mobile-container">
          <div className="thread-wrapper thread-content-scrollable">
            <div className="d-flex align-items-center">
              <span className="thread-meta-arrow mr-3 mt-2">
                <Icon
                  name={`fas fa-chevron-left cursor-pointer`}
                  onClick={(e) => handleNavigateBack(e)}
                />
              </span>

              <h4 className="thread-title fw-semi-bold d-inline w-90">
                {currentThread?.title || ""}
              </h4>

              {/* Copy thread link to clipboard - currently commented out, will be added in future releases */}
              {/* <button
                onClick={handleCopy}
                className="copy-link-btn fw-semi-bold text-nowrap cursor-pointer"
              >
                <Icon className="mr-2" name="copy" />
                {copied ? "Link copied!" : "Copy link"}
              </button> */}
            </div>

            {/* Show all Thread subscribers section */}
            <div className="thread-subscribers mt-2">
              <div className="mr-2">Deltagare: </div>
              <div className="d-flex flex-wrap">
                {subscribers.slice(0, 5).map((s, i, arr) => (
                  <span key={i} className="text-nowrap mr-2 mb-1">
                    {`${s.givenName} ${s.surName}`}
                    {i !== arr.length - 1 && ", "}
                  </span>
                ))}

                <span className="text-nowrap">
                  {subscribers.length > 5 && (
                    <> + {subscribers.length - 5} andra mottagare</>
                  )}
                </span>
              </div>
            </div>

            <hr className="hr-divider" />

            {/* Show text of absent subscribers */}
            {!isReplying && absentSubscribers.length > 0 && (
              <>
                <div className="mt-3 mb-4">
                  <label className="mb-3">Frånvaromeddelanden</label>

                  {absentSubscribers.map((u) => {
                    return (
                      <div className="mt-0" key={u.id}>
                        <b>
                          {u.givenName} {u.surName}
                        </b>
                        :
                        <div className="marginTop out-of-office-message">
                          {u.outOfOfficeMessage.split("\n").map((item, key) => {
                            return (
                              <span key={key}>
                                {item}
                                <br />
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <hr className="hr-divider" />
              </>
            )}

            {isSending && <Loader />}

            {isReplying && (
              <>
                {showConfirmDiscardModal && (
                  <DiscardMessageModal
                    onDismiss={() => setShowConfirmDiscardModal(false)}
                    onCancel={() => setShowConfirmDiscardModal(false)}
                    onConfirm={handleConfirmDiscard}
                  />
                )}
                <CreateThreadMobile
                  ref={createThreadRef}
                  isSending={isSending}
                  defaultRecipients={currentThread.subscribers}
                  onSend={handleOnSend}
                  thread={currentThread}
                  markDirty={markDirty}
                  clearDirty={clearDirty}
                  hideActions={true}
                  onValidationChange={handleValidationChange}
                />
              </>
            )}

            {visibleMessages.map((message: MessageItem, index: number) => (
              <React.Fragment key={message.messageId}>
                <Message
                  key={message.messageId}
                  id={message.messageId}
                  message={message}
                  thread={currentThread}
                />
                {index !== visibleMessages.length - 1 && (
                  <hr className="hr-divider" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Action bar at bottom of component */}
          <div
            className={`thread-action-bar-container ${actionBarFixed ? "fixed-to-viewport" : "fixed-to-component"}`}
          >
            <ThreadActionBar
              mobile
              onReply={() => setIsReplying(true)}
              onMarkAsUnread={() => handleThreadListAction("unread")}
              showMarkAsUnread={!currentThread.unread && !!subscriber}
              subscriber={!!subscriber}
              onCategoryChange={handleCategoryChange}
              onFlagClick={handleFlagClick}
              onArchiveClick={handleArchiveClick}
              onUndoArchiveClick={handleUndoArchiveClick}
              onStatsClick={handleStatsClick}
              // Reply mode props
              isReplying={isReplying}
              onSendReply={() => {
                createThreadRef.current?.sendMessage();
              }}
              onAttach={handleAttachClick}
              onAttachFromArchive={
                isInternalUser() ? handleAttachFromArchiveClick : undefined
              }
              onSaveDraft={handleSaveDraft}
              onDiscardDraft={handleDiscardClicked}
              canSend={canSendMessage}
              isSending={isSending}
            />
          </div>
        </div>
      )}
    </div>
  );
}
